import { Injectable, signal } from '@angular/core';
import { SettingsResource } from '../resource/settings.resource';
import { BehaviorSubject, Subject, tap } from 'rxjs';
import { LocalStorageSettings, SETTINGS_STORAGE_KEY } from '../../nav/local-storage.settings';
import { clearDismissiblePreferences } from '../../nav/dismissible-prefs';
import { GeneralInfo } from '../interfaces/general-info';
import { DefaultValues, OtherLinksMode, OtherNodesMode } from '@maporium-workspace/shared';
import { MapService } from './map.service';
import { isInIframe } from '../../shared/helpers/maporium.validators';
import { LinkTarget } from '../../shared/pipes/link-target/link-target.enum';
import { DOUBLE_TAP_SETTINGS_KEY } from '../../../cytoscape-extensions/double-tap.extension';

export enum LinkViewMode {
  ALL,
  OFF,
  RELATED,
  SELECTION_ENCLOSED_LINKS
}

export const DefaultSidebarWidth = 300;
export const EDGE_VIEW_MODE_KEY = 'edgeViewMode';
export const OTHER_LINKS_MODE_KEY = 'otherLinksMode';
export const OTHER_NODES_MODE_KEY = 'otherNodesMode';
/** Pre-three-state key, still read once so an existing choice is not lost. */
export const LEGACY_OTHER_LINKS_DIMMED_KEY = 'otherLinksDimmed';
/** The application's own entry in the blobs it shares with the smaplets. */
export const APP_SCOPE = 'app';

/**
 * localStorage is shared by the application and every smaplet on the same
 * origin, so each gets its own entry: a smaplet has to open exactly as its
 * author configured it even for a viewer who also uses the application.
 */
export function displayScope(mapId: string, inIframe = isInIframe()): string {
  return inIframe ? `smaplet:${mapId}` : mapId;
}

/** The nodes choice is one application-wide value, but still per smaplet. */
export function nodesDisplayScope(mapId: string | undefined, inIframe = isInIframe()): string {
  return inIframe && mapId ? `smaplet:${mapId}` : APP_SCOPE;
}

/**
 * In "No links" (OFF) every link is hidden outright, so the choice has no
 * effect there; every other mode honours it.
 */
export function resolveOtherLinksMode(mode: LinkViewMode, otherLinksMode: OtherLinksMode): OtherLinksMode {
  return mode === LinkViewMode.OFF ? OtherLinksMode.HIDDEN : otherLinksMode;
}

export function resolveOtherNodesMode(mode: LinkViewMode, otherNodesMode: OtherNodesMode): OtherNodesMode {
  return mode === LinkViewMode.ALL ? OtherNodesMode.SHOWN : otherNodesMode;
}

// Mirrors the current column defaults in apps/maporium/src/app/entities/settings.entity.ts -
// the "factory settings" a reset should restore this dialog's backend-persisted fields to.
export const FACTORY_GENERAL_INFO: Partial<GeneralInfo> = {
  autoAdjustLabelSize: false,
  elementsDimming: 0.5,
  filteringOpacity: DefaultValues.filteringOpacity,
  nodeOverlayOpacity: 0.3,
  nodeDropOverlayOpacity: 0.2,
  nodeDragOpacity: 0.5,
  nodeHighlightColor: '#777777',
  showGrid: false,
  showAlignLines: true,
  gridSize: 15,
  snapToAlignLines: true,
  snapToGrid: false,
  soundVolume: 1,
};

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  public static sharedValues = {
    node: {
      nodeOverlaySelectOpacity: DefaultValues.node.nodeOverlayOpacity,
      nodeDropOverlayOpacity: DefaultValues.node.nodeDropOverlayOpacity,
      nodeDragOpacity: DefaultValues.node.nodeDragOpacity,
      nodeHighlightColor: DefaultValues.node.nodeHighlightColor,
    },
    link: {
      selectedOverlayOpacity: DefaultValues.link.selectedOverlayOpacity,
      selectedOverlayColor: DefaultValues.link.selectedOverlayColor
    },
    /**
     * MAP-1017: opacity for dimmed (non-matching) links, excluded-from-state
     * elements, and filtered-out elements. Read by cy-styles.ts / cytoscape.service.ts.
     */
    filteringOpacity: DefaultValues.filteringOpacity
  }

  /**
   * @deprecated
   */
  public generalInfoChanged = new BehaviorSubject<Partial<GeneralInfo>>({
    autoAdjustLabelSize: false
  });

  /**
   * @deprecated
   *
   */
  public localStorageSettingsChanged = new Subject<LocalStorageSettings>;

  public linkViewModeSignal = signal<LinkViewMode>(LinkViewMode.ALL);
  public otherLinksModeSignal = signal<OtherLinksMode>(OtherLinksMode.HIDDEN);
  public otherNodesModeSignal = signal<OtherNodesMode>(OtherNodesMode.SHOWN);
  /**
   * Updates every time the settings is set to local storage
   */
  public settingsSignal = signal<LocalStorageSettings | null>(null);

  /**
   * Tracks system dark mode preference changes
   */
  public systemDarkModeSignal = signal<boolean>(false);

  constructor(private settingsResource: SettingsResource,
              private mapService: MapService) {
    const localSettings = this.getLocalStorageSettings();
    localSettings.forceOpened = false;
    this.setLocalStorageSettings(localSettings);
    const localLinkViewMode = this.getLocalLinkViewMode();
    this.setLinkViewMode(localLinkViewMode);
    this.setOtherLinksMode(this.getLocalOtherLinksMode());
    this.otherNodesModeSignal.set(this.getLocalOtherNodesMode());

    // Initialize and track system dark mode changes
    this.initializeSystemDarkModeTracking();
  }

  get linkViewMode(): LinkViewMode {
    return this.linkViewModeSignal();
  }

  setLinkViewMode(mode: LinkViewMode) {
    this.setLocalLinkViewMode(mode);
    this.linkViewModeSignal.set(mode);
  }

  getLocalSettingsSignal(): LocalStorageSettings | null {
    return this.settingsSignal();
  }

  /**
   * Reads a per-map lookup blob from localStorage.
   *
   * Guards every way the raw value can fail to be an object: an absent key
   * (getItem -> null, and JSON.parse(null) returns null rather than throwing),
   * the literal string "undefined" left by a JSON.stringify(undefined) write,
   * and any corrupted payload. Callers index the result by map id, so returning
   * anything nullable here throws inside the constructor and takes down app
   * bootstrap on every route.
   */
  private readStoredMapBlob(key: string): Record<string, any> {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined' || raw === 'null') {
      return {};
    }
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  setLocalLinkViewMode(mode: LinkViewMode) {
    const localStoredMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!localStoredMap?.id) {
      return;
    }
    const storedModes = this.readStoredMapBlob(EDGE_VIEW_MODE_KEY);
    storedModes[displayScope(localStoredMap.id)] = mode;
    localStorage.setItem(EDGE_VIEW_MODE_KEY, JSON.stringify(storedModes));
  }

  initLocalLinkViewMode() {
    const localLinkViewMode = this.getLocalLinkViewMode();
    this.setLinkViewMode(localLinkViewMode);
  }

  getLocalLinkViewMode(): LinkViewMode {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap?.id) {
      return LinkViewMode.ALL;
    }
    const stored = this.readStoredMapBlob(EDGE_VIEW_MODE_KEY)[displayScope(currentMap.id)];
    if (stored !== undefined) {
      return stored;
    }
    return isInIframe() ? (currentMap.smapletLinkViewMode ?? LinkViewMode.ALL) as LinkViewMode : LinkViewMode.ALL;
  }

  get otherLinksMode(): OtherLinksMode {
    return this.otherLinksModeSignal();
  }

  /**
   * Same persistence mechanism as setLinkViewMode/setLocalLinkViewMode above:
   * per-map in localStorage, independent selection from the link view mode.
   */
  setOtherLinksMode(mode: OtherLinksMode) {
    this.setLocalOtherLinksMode(mode);
    this.otherLinksModeSignal.set(mode);
  }

  setLocalOtherLinksMode(mode: OtherLinksMode) {
    const localStoredMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!localStoredMap?.id) {
      return;
    }
    const storedModes = this.readStoredMapBlob(OTHER_LINKS_MODE_KEY);
    storedModes[displayScope(localStoredMap.id)] = mode;
    localStorage.setItem(OTHER_LINKS_MODE_KEY, JSON.stringify(storedModes));
  }

  initLocalOtherLinksMode() {
    this.setOtherLinksMode(this.getLocalOtherLinksMode());
  }

  getLocalOtherLinksMode(): OtherLinksMode {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap?.id) {
      return OtherLinksMode.HIDDEN;
    }
    const scope = displayScope(currentMap.id);
    const stored = this.readStoredMapBlob(OTHER_LINKS_MODE_KEY);
    if (stored[scope] !== undefined) {
      return stored[scope];
    }
    if (!isInIframe()) {
      const legacy = this.readStoredMapBlob(LEGACY_OTHER_LINKS_DIMMED_KEY);
      if (legacy[scope] !== undefined) {
        return legacy[scope] ? OtherLinksMode.DIMMED : OtherLinksMode.HIDDEN;
      }
      return OtherLinksMode.HIDDEN;
    }
    const linkViewMode = currentMap.smapletLinkViewMode ?? LinkViewMode.ALL;
    if (linkViewMode === LinkViewMode.RELATED || linkViewMode === LinkViewMode.SELECTION_ENCLOSED_LINKS) {
      return OtherLinksMode.HIDDEN;
    }
    return currentMap.smapletOtherLinksMode ?? OtherLinksMode.HIDDEN;
  }

  get otherNodesMode(): OtherNodesMode {
    return this.otherNodesModeSignal();
  }

  /**
   * In the application this is user state, not map state: one choice that
   * follows the user across smaps and sessions. A smaplet keeps its own.
   */
  setOtherNodesMode(mode: OtherNodesMode) {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    const stored = this.readStoredMapBlob(OTHER_NODES_MODE_KEY);
    stored[nodesDisplayScope(mapId)] = mode;
    localStorage.setItem(OTHER_NODES_MODE_KEY, JSON.stringify(stored));
    this.otherNodesModeSignal.set(mode);
  }

  initLocalOtherNodesMode() {
    this.otherNodesModeSignal.set(this.getLocalOtherNodesMode());
  }

  getLocalOtherNodesMode(): OtherNodesMode {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    const stored = this.readStoredMapBlob(OTHER_NODES_MODE_KEY)[nodesDisplayScope(currentMap?.id)];
    if (stored !== undefined) {
      return stored;
    }
    if (!isInIframe()) {
      return OtherNodesMode.SHOWN;
    }
    const linkViewMode = currentMap?.smapletLinkViewMode ?? LinkViewMode.ALL;
    if (linkViewMode === LinkViewMode.RELATED || linkViewMode === LinkViewMode.SELECTION_ENCLOSED_LINKS) {
      return OtherNodesMode.SHOWN;
    }
    return currentMap?.smapletOtherNodesMode ?? OtherNodesMode.SHOWN;
  }

  getGeneralInfo() {
    return this.settingsResource.getGeneralInfo()
      .pipe(
        tap((generalInfo) => {
          if(generalInfo.nodeOverlayOpacity !== undefined) {
            SettingsService.sharedValues.node.nodeOverlaySelectOpacity = generalInfo.nodeOverlayOpacity;
            SettingsService.sharedValues.link.selectedOverlayOpacity = generalInfo.nodeOverlayOpacity;
            SettingsService.sharedValues.node.nodeDropOverlayOpacity = generalInfo.nodeDropOverlayOpacity;
            SettingsService.sharedValues.node.nodeDragOpacity = generalInfo.nodeDragOpacity;
            SettingsService.sharedValues.node.nodeHighlightColor = generalInfo.nodeHighlightColor;
            SettingsService.sharedValues.link.selectedOverlayColor = generalInfo.nodeHighlightColor;
          }
          this.applyFilteringOpacity(generalInfo.filteringOpacity);
          this.generalInfoChanged.next(generalInfo);
        })
      );
  }

  /**
   * MAP-1017: pushes the filtering opacity to the two places that read it -
   * `sharedValues` (cytoscape style functions in cy-styles.ts /
   * cytoscape.service.ts) and the `--filtering-opacity` CSS variable (the HTML
   * edge-label overlay). Called on load AND on save, so a change applies to the
   * open canvas without a reload.
   */
  applyFilteringOpacity(filteringOpacity: number | undefined | null) {
    if (filteringOpacity === undefined || filteringOpacity === null) return;
    SettingsService.sharedValues.filteringOpacity = filteringOpacity;
    document.documentElement.style.setProperty('--filtering-opacity', String(filteringOpacity));
  }

  isSidebarOpen(): boolean {
    const localStorageSettings: LocalStorageSettings = this.getLocalStorageSettings();
    return localStorageSettings.isSidebarOpen || false;
  }

  storeSidebarWidth(width: number) {
    const localStorageSettings: LocalStorageSettings = this.getLocalStorageSettings();
    localStorageSettings.sidebarWidth = width;
    this.setLocalStorageSettings(localStorageSettings);
  }

  getSidebarWidth(): number {
    const localStorageSettings: LocalStorageSettings = this.getLocalStorageSettings();
    return localStorageSettings.sidebarWidth || DefaultSidebarWidth;
  }

  saveGeneralInfo(generalInfo: Partial<GeneralInfo>) {
    return this.settingsResource.saveGeneralInfo(generalInfo)
      .pipe(
        // MAP-1017: keep the live filtering opacity in step with what was just
        // saved (settings dialog, factory reset) - the style functions and the
        // label overlay read it on every restyle, not from the backend.
        tap((saved) => this.applyFilteringOpacity(saved?.filteringOpacity ?? generalInfo.filteringOpacity))
      );
  }

  toggleSidebar(props?: { forceOpen?: boolean, forceClose?: boolean }) {
    const localStorageSettings: LocalStorageSettings = this.getLocalStorageSettings();
    if (props !== undefined) {
      if (props.forceOpen) {
        localStorageSettings.isSidebarOpen = true;
        localStorageSettings.forceOpened = true;
      } else if (props.forceClose) {
        localStorageSettings.isSidebarOpen = false;
        localStorageSettings.forceOpened = false;
      }
    } else {
      localStorageSettings.isSidebarOpen = !localStorageSettings.isSidebarOpen;
      localStorageSettings.forceOpened = false;
    }
    this.setLocalStorageSettings(localStorageSettings);
  }

  setLocalStorageSettings(settings: LocalStorageSettings) {
    const existingSettings: LocalStorageSettings = JSON.parse(
      localStorage.getItem(SETTINGS_STORAGE_KEY) || '{}'
    );
    const updatedSettings: LocalStorageSettings = {
      ...existingSettings,
      ...settings,
    };
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(updatedSettings));
    this.localStorageSettingsChanged.next(updatedSettings);
    this.settingsSignal.set(updatedSettings);
  }

  getLocalStorageSettings(): LocalStorageSettings {
    const settings = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (settings) {
      return JSON.parse(settings);
    }
    return {
      isSidebarOpen: true,
      isEditMode: false,
      linkTarget: isInIframe() ? LinkTarget.SingleTab : LinkTarget.SingleWindow
    };
  }

  getSidebarWidthReal(): number {
    const matDrawerContainer = document.querySelector('.mat-drawer');

    return matDrawerContainer?.clientWidth || DefaultSidebarWidth;
  }

  getOptimizeLinkSelection(): boolean {
    const localStorageSettings = this.getLocalStorageSettings();
    return localStorageSettings.optimizeLinkSelection ?? false;
  }

  /** MAP-1688: off means an information edit stays on the focused element. */
  getApplyEditsToMultiselection(): boolean {
    return this.getLocalStorageSettings().applyEditsToMultiselection ?? false;
  }

  getOpenInfoPanelOnFocus(): boolean {
    return this.getLocalStorageSettings().openInfoPanelOnFocus ?? true;
  }

  openSidebarOnFocusChange(): void {
    if (!this.getOpenInfoPanelOnFocus() || this.isSidebarOpen()) return;
    this.toggleSidebar();
  }

  resetToFactorySettings() {
    return this.saveGeneralInfo(FACTORY_GENERAL_INFO).pipe(
      tap(() => {
        localStorage.removeItem(SETTINGS_STORAGE_KEY);
        localStorage.removeItem(DOUBLE_TAP_SETTINGS_KEY);
        // MAP-1583 (review round 2): the "do not show this again" opt-outs that
        // live outside the app-settings blob have to go too, otherwise a reset
        // leaves the warning bubbles permanently suppressed.
        clearDismissiblePreferences();
        this.settingsSignal.set(null);
      })
    );
  }

  getAiGenerationLongPressOnly(): boolean {
    const localStorageSettings = this.getLocalStorageSettings();
    return localStorageSettings.aiGenerationLongPressOnly || false;
  }

  setAiGenerationLongPressOnly(longPressOnly: boolean) {
    const localStorageSettings = this.getLocalStorageSettings();
    localStorageSettings.aiGenerationLongPressOnly = longPressOnly;
    this.setLocalStorageSettings(localStorageSettings);
  }

  getDefaultAppearance(): string {
    const localStorageSettings = this.getLocalStorageSettings();
    return localStorageSettings.defaultAppearance || DefaultValues.appearance.default;
  }

  isDarkMode(mapAppearance?: string | null): boolean {
    const effectiveAppearance = mapAppearance ?? this.getDefaultAppearance();
    
    if (effectiveAppearance === 'system') {
      return this.detectSystemDarkMode();
    }
    
    return effectiveAppearance === 'dark';
  }

  getAppearanceAwareColor(lightColor: string, darkColor: string, mapAppearance?: string | null): string {
    return this.isDarkMode(mapAppearance) ? darkColor : lightColor;
  }

  private detectSystemDarkMode(): boolean {
    return this.systemDarkModeSignal();
  }

  private initializeSystemDarkModeTracking(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Set initial value
    this.systemDarkModeSignal.set(mediaQuery.matches);
    
    // Listen for changes
    const handleChange = (e: MediaQueryListEvent) => {
      this.systemDarkModeSignal.set(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
  }
}
